import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { MemberObservationNewThresholdsUpdateInput, NeoInputMode } from '@vivport/backend/types';
import { checkAccountNameAvailability, getLoginLanding, getLoginLandingOptions, getSignupPlanOffer, markNeoIntroSeen, sendSignupEmailCode, updateAccountPreferences, updateLoginLanding, updateMemberAutoAnchorUploads, updateMemberLandingView, updateMemberNeoDefaultInputMode, updateMemberObservationNewThresholds, verifySignupEmailCode } from '../api/auth-api';
import { useSpeak2MeStore } from '../store/speak2meStore';
import { checkAuth, login, logout, refreshToken, signupCheckout, signupComplete } from '../utils/auth-utils';
import { clearTokens } from '../utils/token-utils';

/**
 * Auth Query Keys
 * Centralized query key factory for auth-related React Query hooks
 */

export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  login: () => [...authKeys.all, 'login'] as const,
  logout: () => [...authKeys.all, 'logout'] as const,
  refresh: () => [...authKeys.all, 'refresh'] as const,
  mfa: () => [...authKeys.all, 'mfa'] as const,
  passkey: () => [...authKeys.all, 'passkey'] as const,
  passwordReset: () => [...authKeys.all, 'passwordReset'] as const,
  invitation: () => [...authKeys.all, 'invitation'] as const,
  signup: () => [...authKeys.all, 'signup'] as const,
  loginLanding: () => [...authKeys.all, 'loginLanding'] as const,
  loginLandingOptions: (memberId: number) => [...authKeys.loginLanding(), 'options', memberId] as const,
};

/**
 * Check authentication status
 * Returns user data if authenticated, null if not
 */
export const useCheckAuth = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: checkAuth,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof AxiosError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
};

export const MIN_ACCOUNT_NAME_CHECK_LENGTH = 3;

/**
 * Live account-name availability check (debounce the input before passing it in)
 */
export const useAccountNameAvailability = (accountName: string) => {
  const trimmed = accountName.trim();
  return useQuery({
    queryKey: [...authKeys.all, 'account-name-availability', trimmed],
    queryFn: () => checkAccountNameAvailability({ accountName: trimmed }),
    enabled: trimmed.length >= MIN_ACCOUNT_NAME_CHECK_LENGTH,
    staleTime: 30_000,
    retry: false,
  });
};

/**
 * Login mutation
 * Handles both normal login and MFA flow
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  const clearSpeak2MeState = () => {
    useSpeak2MeStore.setState({
      isOpen: false,
      resumeNeoChatId: null,
      resumeProvenance: null,
    });
    localStorage.removeItem('speak2meData');
  };

  return useMutation({
    mutationKey: authKeys.login(),
    mutationFn: login,
    onMutate: async () => {
      // Prevent stale in-flight auth checks from racing and overwriting fresh login state.
      await queryClient.cancelQueries({ queryKey: authKeys.user() });
    },
    onSuccess: (data) => {
      // Ensure login starts with a clean Neo chat state.
      clearSpeak2MeState();

      // If MFA is required, don't invalidate queries yet
      if ('mfaRequired' in data) {
        return;
      }

      // Normal login success: seed authenticated user immediately.
      // This avoids a transient logged-out state while /auth/user refetches.
      queryClient.setQueryData(authKeys.user(), data);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: (_error) => {
      // Clear any existing tokens on login error
      clearTokens();
      localStorage.removeItem('memberId');
      queryClient.setQueryData(authKeys.user(), null);
    },
  });
};

/**
 * Public plan offer for the signup page (discount resolved by email when given)
 */
export const useSignupPlanOffer = (email?: string) => {
  const trimmed = email?.trim() ?? '';
  return useQuery({
    queryKey: [...authKeys.signup(), 'plan-offer', trimmed],
    queryFn: () => getSignupPlanOffer(trimmed || undefined),
    staleTime: 60_000,
    retry: 1,
  });
};

/**
 * Send a signup email verification code
 */
export const useSendSignupEmailCode = () => {
  return useMutation({
    mutationKey: [...authKeys.signup(), 'email-code', 'send'],
    mutationFn: sendSignupEmailCode,
  });
};

/**
 * Verify a signup email verification code
 */
export const useVerifySignupEmailCode = () => {
  return useMutation({
    mutationKey: [...authKeys.signup(), 'email-code', 'verify'],
    mutationFn: verifySignupEmailCode,
  });
};

/**
 * Starts signup: stores the pending signup and redirects to Stripe checkout,
 * or logs straight in when the email has a 100% discount
 */
export const useSignupCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: authKeys.signup(),
    mutationFn: signupCheckout,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authKeys.user() });
    },
    onSuccess: (outcome) => {
      if (outcome.kind === 'complete') {
        queryClient.setQueryData(authKeys.user(), outcome.auth);
        queryClient.invalidateQueries({ queryKey: authKeys.user() });
      }
    },
    onError: () => {
      clearTokens();
      localStorage.removeItem('memberId');
      queryClient.setQueryData(authKeys.user(), null);
    },
  });
};

/**
 * Complete signup after returning from Stripe checkout
 */
export const useSignupComplete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...authKeys.signup(), 'complete'],
    mutationFn: signupComplete,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authKeys.user() });
    },
    onSuccess: (auth) => {
      queryClient.setQueryData(authKeys.user(), auth);
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Logout mutation
 * Clears tokens and invalidates user query
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: authKeys.logout(),
    mutationFn: logout,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authKeys.user() });
    },
    onSuccess: () => {
      // Immediately mark user as unauthenticated for all consumers.
      queryClient.setQueryData(authKeys.user(), null);
      // Clear all auth-related queries
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
    onError: () => {
      // Even if logout API fails, clear local state
      clearTokens();
      localStorage.removeItem('memberId');
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
  });
};

/**
 * Refresh token mutation
 * Updates tokens and invalidates user query
 */
export const useRefreshToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: authKeys.refresh(),
    mutationFn: refreshToken,
    onSuccess: () => {
      // Invalidate user query to refetch with new token
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: () => {
      // Clear tokens on refresh failure
      clearTokens();
      localStorage.removeItem('memberId');
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
  });
};

/**
 * Mark the Neo first-login introduction as seen for the current account
 */
export const useMarkNeoIntroSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNeoIntroSeen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Update account-level display preferences for the current account
 */
export const useUpdateAccountPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAccountPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * The account's login landing preference (landing member + view)
 */
export const useLoginLanding = () => {
  return useQuery({
    queryKey: authKeys.loginLanding(),
    queryFn: getLoginLanding,
  });
};

/**
 * Landing-view options (tabs + visible saved filters) for one of the account's members
 */
export const useLoginLandingOptions = (memberId: number | null) => {
  return useQuery({
    queryKey: memberId !== null ? authKeys.loginLandingOptions(memberId) : [...authKeys.loginLanding(), 'options', 'disabled'] as const,
    queryFn: () => getLoginLandingOptions(memberId!),
    enabled: memberId !== null,
  });
};

/**
 * Update the account's login landing member preference
 */
export const useUpdateLoginLanding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateLoginLanding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.loginLanding() });
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Update a member's landing view (opened on login or member switch)
 */
export const useUpdateMemberLandingView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, filterId }: { memberId: number; filterId: number | null }) =>
      updateMemberLandingView(memberId, { filterId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.loginLanding() });
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Update whether a member's uploads are filed automatically by the platform
 */
export const useUpdateMemberAutoAnchorUploads = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, autoAnchorUploads }: { memberId: number; autoAnchorUploads: boolean }) =>
      updateMemberAutoAnchorUploads(memberId, { autoAnchorUploads }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.loginLanding() });
    },
  });
};

/**
 * Update which input mode a member's Neo chat opens in from the Speak2Me button
 */
export const useUpdateMemberNeoDefaultInputMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, neoDefaultInputMode }: { memberId: number; neoDefaultInputMode: NeoInputMode }) =>
      updateMemberNeoDefaultInputMode(memberId, { neoDefaultInputMode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.loginLanding() });
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Update when Observation Capture defaults to a new Observation for a member
 */
export const useUpdateMemberObservationNewThresholds = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, ...input }: { memberId: number } & MemberObservationNewThresholdsUpdateInput) =>
      updateMemberObservationNewThresholds(memberId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.loginLanding() });
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
  });
};

/**
 * Get auth data hook
 * Returns user data with loading and authentication status
 * This is a convenience hook that wraps useCheckAuth
 */
export const useAuthData = () => {
  const userQuery = useCheckAuth();
  
  return {
    ...userQuery,
    isAuthenticated: !!userQuery.data,
    isLoading: userQuery.isLoading,
    account: userQuery.data?.account,
    member: userQuery.data?.selectedMember,
    members: userQuery.data?.members,
  };
};